import type { User } from "@supabase/supabase-js";
import { useIsRestoring } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import {
  ChefHat,
  ChevronDown,
  ChevronUp,
  DoorOpen,
  Download,
  Eraser,
  Home,
  Info,
  ListFilter,
  LogOut,
  Package,
  Plus,
  Refrigerator,
  Settings,
  Settings2,
  ShelvingUnit,
  ShoppingCart,
  Snowflake,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { PresenceProvider } from "../contexts/PresenceContext";
import { useQueryClient } from "../hooks/useDataCache";
import { useHousehold } from "../hooks/useHousehold";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { useInventory } from "../hooks/useInventory";
import { useLicense } from "../hooks/useLicense";
import { markExplicitSignOut } from "../lib/authSignals";
import {
  loadSortPreference,
  type SortPreference,
  saveSortPreference,
} from "../lib/sorting";
import { supabase } from "../lib/supabase";
import HouseholdSettings from "./HouseholdSettings";
import HouseholdSwitcher from "./HouseholdSwitcher";
import InventoryTab from "./InventoryTab";
import ItemForm from "./ItemForm";
import LicenseRequired from "./LicenseRequired";
import RecipeSearch from "./RecipeSearch";
import SearchInput from "./SearchInput";
import ShoppingList from "./ShoppingList";
import SortControl from "./SortControl";
import ThemeToggle from "./ThemeToggle";

// Top-level dashboard component that orchestrates the main app views
// - provides the PresenceProvider wrapper
// - manages top navigation tabs (inventory, shopping, recipes)
// - coordinates modals, search state, and household settings
export default function Dashboard() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeTab: "inventory" | "shopping" | "recipes" = pathname.includes(
    "shopping",
  )
    ? "shopping"
    : pathname.includes("recipes")
      ? "recipes"
      : "inventory";

  const [showAddForm, setShowAddForm] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const {
    household,
    isLoading: householdLoading,
    isFetching: householdFetching,
  } = useHousehold();
  const {
    license,
    isLoading: licenseLoading,
    isFetching: licenseFetching,
  } = useLicense();
  const isRestoring = useIsRestoring();
  const [showOtherTooltip, setShowOtherTooltip] = useState(false);
  const [showHouseholdSettings, setShowHouseholdSettings] = useState(false);
  const [loadingHouseholdSettings, setLoadingHouseholdSettings] =
    useState(false);

  const [selectedInventoryCategories, setSelectedInventoryCategories] =
    useState<string[]>(() => {
      const stored = localStorage.getItem("inventoryFilters");
      return stored ? JSON.parse(stored) : [];
    });
  const [inventorySearchQuery, setInventorySearchQuery] = useState("");
  const [showInventoryControls, setShowInventoryControls] = useState(() => {
    const stored = localStorage.getItem("inventoryControlsExpanded");
    return stored === null ? true : stored === "true";
  });
  const [inventorySort, setInventorySort] = useState<SortPreference>(() =>
    loadSortPreference("inventorySort"),
  );
  const queryClient = useQueryClient();
  const { canInstall, install } = useInstallPrompt();
  const inventoryTabRef = useRef<HTMLButtonElement>(null);
  const shoppingTabRef = useRef<HTMLButtonElement>(null);
  const recipesTabRef = useRef<HTMLButtonElement>(null);

  // Save and restore scroll position when switching tabs
  const savedScrollY = useRef<
    Partial<Record<"inventory" | "shopping" | "recipes", number>>
  >({});
  const prevActiveTab = useRef(activeTab);
  useEffect(() => {
    const prev = prevActiveTab.current;
    if (prev === activeTab) return;
    savedScrollY.current[prev] = window.scrollY;
    window.scrollTo({
      top: savedScrollY.current[activeTab] ?? 0,
      behavior: "instant",
    });
    prevActiveTab.current = activeTab;
  }, [activeTab]);

  // Get all inventory items to build the category filter
  const {
    items: allInventoryItems = [],
    isLoading: inventoryLoading,
    isFetching: inventoryFetching,
  } = useInventory("all");

  // True whenever any background data fetch is in flight — drives the single
  // global loading bar under the header instead of per-component spinners.
  const isGloballyLoading =
    isRestoring ||
    householdLoading ||
    householdFetching ||
    licenseLoading ||
    licenseFetching ||
    loadingHouseholdSettings ||
    inventoryLoading ||
    inventoryFetching;

  // True only when all household fetching has fully settled and there is
  // definitively no household. While any fetch is in flight we show the spinner
  // instead of "No Household Found" to prevent flashing on resume/invalidation.
  const householdDefinitelyAbsent =
    !household &&
    !isRestoring &&
    !householdLoading &&
    !householdFetching &&
    !licenseLoading &&
    !licenseFetching;

  // Refresh data when the app returns from the background (e.g. phone wake).
  // We deliberately do NOT invalidate USER_HOUSEHOLD_ID_KEY or the household
  // record here — those are protected by last-known-good refs in useUser and
  // useHousehold and will re-fetch in the background on their own stale timer.
  // Invalidating them here caused races where a slow/failed re-fetch would
  // clear the household ID and flash "No Household Found".
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && household?.id) {
        queryClient.invalidateQueries({ queryKey: ["inventory"] });
        queryClient.invalidateQueries({ queryKey: ["shoppingList"] });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [household?.id, queryClient]);

  const fridgeCount = useMemo(
    () => allInventoryItems.filter((i) => i.location === "fridge").length,
    [allInventoryItems],
  );
  const freezerCount = useMemo(
    () => allInventoryItems.filter((i) => i.location === "freezer").length,
    [allInventoryItems],
  );
  const pantryCount = useMemo(
    () => allInventoryItems.filter((i) => i.location === "pantry").length,
    [allInventoryItems],
  );
  const otherCount = useMemo(
    () => allInventoryItems.filter((i) => i.location === "other").length,
    [allInventoryItems],
  );

  // Derive available inventory categories from all items for the filter UI
  const availableInventoryCategories = useMemo(() => {
    return [
      ...new Set(
        allInventoryItems
          .map((item) => item.category)
          .filter((c): c is string => !!c),
      ),
    ].sort();
  }, [allInventoryItems]);

  // Derive filtered categories to only show those that exist
  const filteredSelectedCategories = useMemo(() => {
    return selectedInventoryCategories.filter((cat) =>
      availableInventoryCategories.includes(cat),
    );
  }, [selectedInventoryCategories, availableInventoryCategories]);

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  // Keep the URL in sync with the license state:
  // - No license + no household + settled → navigate to /purchase so the URL is truthful
  // - Has license but sitting on /purchase → navigate to /inventory
  // NOTE: Users who were invited and joined a household do NOT need a license
  // (they are members, not household creators). Only redirect to /purchase when
  // there is definitively no household either.
  useEffect(() => {
    const settled =
      !licenseLoading &&
      !licenseFetching &&
      !isRestoring &&
      !householdLoading &&
      !householdFetching;
    if (!settled) return;
    // Guard: if license is still undefined (cache miss during load) don't
    // redirect — wait for the next render when it has a real value.
    if (license === undefined) return;
    // An invited member has no license but already belongs to a household —
    // they should never see the pricing screen.
    if (!license.canCreateHousehold && !household && pathname !== "/purchase") {
      void router.navigate({ to: "/purchase", replace: true });
    } else if (license.canCreateHousehold && pathname === "/purchase") {
      void router.navigate({ to: "/inventory", replace: true });
    } else if (household && pathname === "/purchase") {
      // Invited member landed on /purchase — redirect them to inventory.
      void router.navigate({ to: "/inventory", replace: true });
    }
  }, [
    licenseLoading,
    licenseFetching,
    isRestoring,
    householdLoading,
    householdFetching,
    license,
    household,
    pathname,
    router,
  ]);

  // Persist selected inventory categories to localStorage
  useEffect(() => {
    localStorage.setItem(
      "inventoryFilters",
      JSON.stringify(selectedInventoryCategories),
    );
  }, [selectedInventoryCategories]);

  // Persist selected inventory sort preference to localStorage
  useEffect(() => {
    saveSortPreference("inventorySort", inventorySort);
  }, [inventorySort]);

  // Persist expanded/collapsed state of the sort/filter/search controls
  useEffect(() => {
    localStorage.setItem(
      "inventoryControlsExpanded",
      String(showInventoryControls),
    );
  }, [showInventoryControls]);

  const toggleInventoryCategory = (category: string) => {
    setSelectedInventoryCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const handleLogout = async () => {
    markExplicitSignOut();
    await supabase.auth.signOut();
    queryClient.clear();

    // Clear invitation token from URL if present
    const params = new URLSearchParams(window.location.search);
    if (params.has("invitation")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  const handleItemAdded = () => {
    setShowAddForm(false);
  };

  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const tabs: ("inventory" | "shopping" | "recipes")[] = [
      "inventory",
      "shopping",
      "recipes",
    ];
    const tabRefs = {
      inventory: inventoryTabRef,
      shopping: shoppingTabRef,
      recipes: recipesTabRef,
    };
    const currentIndex = tabs.indexOf(activeTab);

    let newTab: "inventory" | "shopping" | "recipes" | null = null;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % tabs.length;
      newTab = tabs[nextIndex];
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      newTab = tabs[prevIndex];
    } else if (e.key === "Home") {
      e.preventDefault();
      newTab = tabs[0];
    } else if (e.key === "End") {
      e.preventDefault();
      newTab = tabs[tabs.length - 1];
    }

    if (newTab) {
      void router.navigate({ to: `/${newTab}` });
      // Focus the newly selected tab after state updates
      const tabToFocus = newTab;
      setTimeout(() => {
        tabRefs[tabToFocus]?.current?.focus();
      }, 0);
    }
  };

  return (
    <PresenceProvider>
      <div className="bg-gradient-animated pb-safe min-h-screen">
        {/* Global loading bar — sits just above the header, below the status bar */}
        <div
          role="status"
          aria-label="Loading"
          aria-live="polite"
          className={`fixed right-0 left-0 z-50 h-0.5 overflow-hidden transition-opacity duration-300 ${
            isGloballyLoading ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          style={{ top: "env(safe-area-inset-top)" }}
        >
          <div className="animate-loading-bar h-full w-full bg-blue-500 dark:bg-blue-400" />
        </div>
        <a
          href="#main-content"
          className="sr-only bg-blue-600 text-white focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg focus-visible:shadow-[0_0_0_2px_rgb(var(--color-bg-primary)),0_0_0_4px_rgb(59_130_246)] dark:focus:bg-blue-500 dark:focus-visible:shadow-[0_0_0_2px_rgb(31_41_55),0_0_0_4px_rgb(59_130_246)]"
        >
          Skip to main content
        </a>
        <header className="pt-safe sticky top-0 z-40 bg-white shadow-sm transition-colors duration-300 dark:bg-gray-800">
          <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
            <div
              className={`flex items-center justify-between ${!household ? "pb-4" : ""}`}
            >
              <div className="flex items-center gap-3">
                <img
                  src="/favicon-browser.svg"
                  alt="Pantry logo"
                  className="h-8 w-8"
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                    Pantry
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {user && (
                  <span className="hidden border-l border-blue-600 pl-2 text-xs text-gray-600 sm:block dark:border-blue-400 dark:text-gray-400">
                    {user.email}
                    {household && <HouseholdSwitcher />}
                  </span>
                )}
                {household && (
                  <button
                    type="button"
                    onClick={async () => {
                      setLoadingHouseholdSettings(true);
                      await new Promise((resolve) => setTimeout(resolve, 100));
                      setShowHouseholdSettings(true);
                    }}
                    className="flex items-center gap-4 rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                    aria-label="Household Settings"
                    disabled={loadingHouseholdSettings}
                  >
                    <Settings
                      className={`h-5 w-5 ${loadingHouseholdSettings ? "animate-spin" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-4 rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                  aria-label="Logout"
                >
                  <LogOut className="h-5 w-5" aria-hidden="true" />
                </button>
                {canInstall && (
                  <button
                    type="button"
                    onClick={install}
                    className="flex items-center gap-4 rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                    aria-label="Install app"
                    title="Install app"
                  >
                    <Download className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
                <ThemeToggle />
              </div>
            </div>

            {household && (
              <nav className="mt-3" aria-label="Main navigation">
                <div role="tablist" className="flex gap-4">
                  <button
                    ref={inventoryTabRef}
                    type="button"
                    role="tab"
                    id="inventory-tab"
                    aria-selected={activeTab === "inventory"}
                    aria-controls="inventory-panel"
                    tabIndex={activeTab === "inventory" ? 0 : -1}
                    onClick={() => void router.navigate({ to: "/inventory" })}
                    onKeyDown={handleTabKeyDown}
                    className={`group flex flex-1 justify-center rounded-t-lg border-b-2 px-1 pt-3 pb-3 font-medium sm:flex-initial sm:px-2 ${
                      activeTab === "inventory"
                        ? "border-blue-600 dark:border-blue-400"
                        : "border-transparent"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 transition-colors ${
                        activeTab === "inventory"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-600 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-gray-200"
                      }`}
                    >
                      <Package className="h-4 w-4" />
                      Inventory
                    </div>
                  </button>
                  <button
                    ref={shoppingTabRef}
                    type="button"
                    role="tab"
                    id="shopping-tab"
                    aria-selected={activeTab === "shopping"}
                    aria-controls="shopping-panel"
                    tabIndex={activeTab === "shopping" ? 0 : -1}
                    onClick={() => void router.navigate({ to: "/shopping" })}
                    onKeyDown={handleTabKeyDown}
                    className={`group flex flex-1 justify-center rounded-t-lg border-b-2 px-1 pt-3 pb-3 font-medium sm:flex-initial sm:px-2 ${
                      activeTab === "shopping"
                        ? "border-blue-600 dark:border-blue-400"
                        : "border-transparent"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 transition-colors ${
                        activeTab === "shopping"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-600 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-gray-200"
                      }`}
                    >
                      <ShoppingCart className="h-4 w-4" />
                      Shopping
                    </div>
                  </button>
                  <button
                    ref={recipesTabRef}
                    type="button"
                    role="tab"
                    id="recipes-tab"
                    aria-selected={activeTab === "recipes"}
                    aria-controls="recipes-panel"
                    tabIndex={activeTab === "recipes" ? 0 : -1}
                    onClick={() => void router.navigate({ to: "/recipes" })}
                    onKeyDown={handleTabKeyDown}
                    className={`group flex flex-1 justify-center rounded-t-lg border-b-2 px-1 pt-3 pb-3 font-medium sm:flex-initial sm:px-2 ${
                      activeTab === "recipes"
                        ? "border-green-600 dark:border-green-400"
                        : "border-transparent"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-2 transition-colors ${
                        activeTab === "recipes"
                          ? "text-green-600 dark:text-green-400"
                          : "text-gray-600 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-gray-200"
                      }`}
                    >
                      <ChefHat className="h-4 w-4" />
                      Recipes
                    </div>
                  </button>
                </div>
              </nav>
            )}
          </div>
        </header>

        <main
          id="main-content"
          className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"
        >
          {(pathname === "/purchase" && !household) ||
          (householdDefinitelyAbsent && !license?.canCreateHousehold) ? (
            <LicenseRequired />
          ) : householdDefinitelyAbsent ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Home className="mb-4 h-16 w-16 text-gray-400 dark:text-gray-600" />
              <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-gray-100">
                No Household Found
              </h2>
              <p className="mb-6 text-center text-gray-600 dark:text-gray-400">
                Create or join a household to use Pantry.
              </p>
              <button
                type="button"
                onClick={async () => {
                  setLoadingHouseholdSettings(true);
                  await new Promise((resolve) => setTimeout(resolve, 100));
                  setShowHouseholdSettings(true);
                }}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-md hover:bg-blue-700 hover:shadow-lg dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                <Home className="h-5 w-5" />
                Create Household
              </button>
            </div>
          ) : (
            <>
              <div
                role="tabpanel"
                id="inventory-panel"
                aria-labelledby="inventory-tab"
                aria-hidden={activeTab !== "inventory"}
                className={`space-y-6 ${activeTab !== "inventory" ? "hidden" : ""}`}
              >
                <div className="space-y-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                        Your Inventory
                      </h2>
                      <p className="mt-1 text-gray-600 dark:text-gray-400">
                        Manage your items across storage locations
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddForm(true)}
                      className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-md hover:bg-blue-700 hover:shadow-lg focus-visible:shadow-[0_0_0_2px_rgb(var(--color-bg-primary)),0_0_0_4px_rgb(59_130_246)] dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus-visible:shadow-[0_0_0_2px_rgb(31_41_55),0_0_0_4px_rgb(59_130_246)]"
                    >
                      <Plus className="h-5 w-5" />
                      Add Item
                    </button>
                  </div>

                  {allInventoryItems.length > 0 && (
                    <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
                      <div className="flex items-center justify-between">
                        <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-800 dark:text-gray-100">
                          <Settings2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          Item Controls
                        </h3>
                        <button
                          type="button"
                          onClick={() => setShowInventoryControls((v) => !v)}
                          className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                          aria-expanded={showInventoryControls}
                          aria-controls="inventory-controls-panel"
                          aria-label={
                            showInventoryControls
                              ? "Collapse sort, filter, and search options"
                              : "Expand sort, filter, and search options"
                          }
                        >
                          {showInventoryControls ? (
                            <ChevronUp className="h-5 w-5" aria-hidden="true" />
                          ) : (
                            <ChevronDown
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      </div>

                      {showInventoryControls && (
                        <div
                          id="inventory-controls-panel"
                          className="mt-6 space-y-6"
                        >
                          <SortControl
                            value={inventorySort}
                            onChange={setInventorySort}
                            legend="Inventory sort order"
                          />

                          {availableInventoryCategories.length > 0 && (
                            <fieldset className="border-t border-gray-200 pt-6 dark:border-gray-700">
                              <legend className="sr-only">
                                Category Filter
                              </legend>
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <h3 className="text-md flex items-center gap-2 font-bold text-gray-800 dark:text-gray-100">
                                  <ListFilter className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                                  Filter:
                                </h3>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedInventoryCategories([])
                                  }
                                  className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium transition ${
                                    filteredSelectedCategories.length > 0
                                      ? "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                                      : "pointer-events-none invisible opacity-0"
                                  }`}
                                  aria-label="Clear all filters"
                                >
                                  <Eraser
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                  />
                                  Clear All
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {availableInventoryCategories.map((cat) => (
                                  <button
                                    type="button"
                                    key={cat}
                                    onClick={() => toggleInventoryCategory(cat)}
                                    aria-pressed={filteredSelectedCategories.includes(
                                      cat,
                                    )}
                                    className={`rounded-full px-3 py-1 text-sm font-medium capitalize transition ${
                                      filteredSelectedCategories.includes(cat)
                                        ? "bg-blue-600 text-white dark:bg-blue-500"
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                                    }`}
                                  >
                                    {cat.replace("_", " ")}
                                  </button>
                                ))}
                              </div>
                            </fieldset>
                          )}

                          <SearchInput
                            id="inventory-search"
                            label="Search inventory items"
                            placeholder="Search items..."
                            value={inventorySearchQuery}
                            onChange={setInventorySearchQuery}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
                      <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
                        <Refrigerator className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        Fridge ({fridgeCount})
                      </h3>
                      <InventoryTab
                        location="fridge"
                        selectedCategories={filteredSelectedCategories}
                        searchQuery={inventorySearchQuery}
                        sort={inventorySort}
                      />
                    </div>

                    <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
                      <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
                        <Snowflake className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                        Freezer ({freezerCount})
                      </h3>
                      <InventoryTab
                        location="freezer"
                        selectedCategories={filteredSelectedCategories}
                        searchQuery={inventorySearchQuery}
                        sort={inventorySort}
                      />
                    </div>

                    <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
                      <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
                        <DoorOpen className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        Pantry ({pantryCount})
                      </h3>
                      <InventoryTab
                        location="pantry"
                        selectedCategories={filteredSelectedCategories}
                        searchQuery={inventorySearchQuery}
                        sort={inventorySort}
                      />
                    </div>

                    <div className="relative rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
                      <div className="absolute top-4 right-4">
                        <button
                          type="button"
                          onClick={() => setShowOtherTooltip((v) => !v)}
                          onBlur={() => setShowOtherTooltip(false)}
                          className="rounded-full p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          aria-label="Items not included in recipe search"
                          aria-expanded={showOtherTooltip}
                        >
                          <Info className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <div
                          className={`pointer-events-none absolute -right-5 bottom-9 z-10 w-52 rounded-lg bg-gray-800 px-3 py-2 text-xs text-white shadow-lg transition-opacity duration-150 dark:bg-gray-900 ${
                            showOtherTooltip ? "opacity-100" : "opacity-0"
                          }`}
                        >
                          Items in "Other" are not included in recipe search
                          <div className="absolute right-6 -bottom-1.5 h-3 w-3 rotate-45 bg-gray-800 dark:bg-gray-900" />
                        </div>
                      </div>
                      <div className="mb-4">
                        <h3 className="flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
                          <ShelvingUnit className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          Other ({otherCount})
                        </h3>
                      </div>
                      <InventoryTab
                        location="other"
                        selectedCategories={filteredSelectedCategories}
                        searchQuery={inventorySearchQuery}
                        sort={inventorySort}
                      />
                    </div>
                  </div>

                  {showAddForm && (
                    <ItemForm
                      onClose={() => setShowAddForm(false)}
                      onItemAdded={handleItemAdded}
                    />
                  )}
                </div>
              </div>

              <div
                role="tabpanel"
                id="shopping-panel"
                aria-labelledby="shopping-tab"
                aria-hidden={activeTab !== "shopping"}
                className={activeTab !== "shopping" ? "hidden" : ""}
              >
                <ShoppingList />
              </div>

              <div
                role="tabpanel"
                id="recipes-panel"
                aria-labelledby="recipes-tab"
                aria-hidden={activeTab !== "recipes"}
                className={activeTab !== "recipes" ? "hidden" : ""}
              >
                <RecipeSearch />
              </div>
            </>
          )}
        </main>

        {showHouseholdSettings && (
          <HouseholdSettings
            onClose={() => {
              setShowHouseholdSettings(false);
              setLoadingHouseholdSettings(false);
            }}
            onLoadingChange={setLoadingHouseholdSettings}
          />
        )}
      </div>
    </PresenceProvider>
  );
}
